"use client";

import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { QuickBooksActions } from "@/components/quickbooks/connection/QuickBooksActions";
import { QuickBooksInvoiceViewer } from "@/components/quickbooks/invoice/QuickBooksInvoiceViewer";
import { useRoleCheck } from "@/lib/hooks/auth/useRoleCheck";
import { useContactQuickBooksData } from "@/features/companies/hooks/company-quickbooks-hooks";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { RecurringTransaction } from "@/lib/types/recurring-transactions";

export const RecurringTransactionQuickBooksDisplay = React.memo(
  ({ item }: TabContentProps<RecurringTransaction>) => {
    const { isAdmin } = useRoleCheck();

    const { data: qbData } = useContactQuickBooksData(item.customer_id ?? "");

    if (!isAdmin) {
      return (
        <Card>
          <CardContent>
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Access Restricted</h3>
                <p className="text-gray-600 dark:text-gray-400">You need admin privileges to view QuickBooks information.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (!item.customer_id && !item.company_id) {
      return (
        <Card>
          <CardContent>
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">No Customer or Company</h3>
                <p className="text-gray-600 dark:text-gray-400">Link a customer or company to view QuickBooks information.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    const customerName = item.company_name || (item.customer as any)?.name || "Unknown Customer";

    return (
      <div className="space-y-4">
        <QuickBooksActions
          qbCustomerId={qbData?.quickbooksCustomerRef || undefined}
          qbDisplayName={qbData?.quickbooksCustomerName || customerName}
          contactId={item.customer_id}
          companyId={item.company_id}
          companyName={item.company_name}
        />

        <QuickBooksInvoiceViewer
          contactId={item.customer_id}
          customerName={customerName}
        />
      </div>
    );
  }
);

RecurringTransactionQuickBooksDisplay.displayName = "RecurringTransactionQuickBooksDisplay";
